import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { api } from "../../lib/api";

import { MobileShell } from "../shell/MobileShell";
import { etichetta } from "../common/etichette";
import { localeCorrente } from "../../i18n";
import { tr } from "../../i18n";
interface AuditItem {
  timestamp: string;
  tipo: string;
  dominio: string;
  actor_user_id: string | null;
  entita_tipo: string;
  entita_id: string | null;
  descrizione: string | null;
  dettagli: Record<string, unknown> | null;
}

interface AuditResponse {
  items: AuditItem[];
  total_returned: number;
  has_more: boolean;
}

const DOMINIO_COLOR: Record<string, string> = {
  auth: "bg-info/20 text-info",
  mercato: "bg-warning/20 text-warning",
  budget: "bg-success/20 text-success",
  tesoreria: "bg-success/20 text-success",
  voti: "bg-accent/20 text-accent",
  squadre: "bg-fg-muted/20 text-fg-muted",
  transfermarkt: "bg-danger/20 text-danger",
};

export function AuditLogPage() {
  useAuth(); // session check
  const [filterDominio, setFilterDominio] = useState<string>("");
  const [filterTipo, setFilterTipo] = useState<string>("");
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "audit", filterDominio, filterTipo, offset],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterDominio) params.set("dominio", filterDominio);
      if (filterTipo) params.set("tipo", filterTipo);
      params.set("limit", String(LIMIT));
      params.set("offset", String(offset));
      const res = await api.get<AuditResponse>(`/admin/audit?${params}`);
      return res.data;
    },
  });

  return (
    <MobileShell logoSuffix="AUDIT LOG">
      <h2 className="font-display text-3xl text-accent tracking-wide">
        {tr("Audit log aggregato")}
      </h2>

      <div className="card grid grid-cols-1 md:grid-cols-3 gap-3">
        <select
          value={filterDominio}
          onChange={(e) => {
            setFilterDominio(e.target.value);
            setOffset(0);
          }}
          className="px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg"
        >
          <option value="">{tr("— tutti i domini —")}</option>
          <option value="auth">{"auth"}</option>
          <option value="mercato">{"mercato"}</option>
          <option value="budget">{"budget"}</option>
          <option value="tesoreria">{"tesoreria"}</option>
          <option value="voti">{"voti"}</option>
          <option value="squadre">{"squadre"}</option>
          <option value="transfermarkt">{"transfermarkt"}</option>
        </select>
        <input
          type="text"
          placeholder={tr("filtra per tipo (es. mercato_proposta)")}
          value={filterTipo}
          onChange={(e) => {
            setFilterTipo(e.target.value);
            setOffset(0);
          }}
          className="px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg font-mono text-sm"
        />
        <div className="text-fg-muted text-sm flex items-center justify-end">
          {data && tr("{{v1}} eventi mostrati", { v1: data.total_returned })}
        </div>
      </div>

      {isLoading && <div className="card text-fg-muted">{tr("Caricamento…")}</div>}
      {error && <div className="card text-danger">Errore: {String(error)}</div>}

      {data && (
        <div className="card p-0 overflow-hidden">
          {data.items.length === 0 && (
            <div className="p-6 text-center text-fg-muted">
              {tr("Nessun evento per i filtri selezionati.")}
            </div>
          )}
          {data.items.map((it, i) => (
            <div
              key={`${it.timestamp}-${i}`}
              className="flex flex-col gap-1 p-3 border-b border-primary/10 last:border-b-0"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`pill ${DOMINIO_COLOR[it.dominio] || "bg-fg-muted/20 text-fg-muted"}`}
                >
                  {it.dominio}
                </span>
                <span className="font-mono text-xs text-accent">{etichetta(it.tipo)}</span>
                <span className="text-xs text-fg-muted ml-auto">
                  {new Date(it.timestamp).toLocaleString(localeCorrente())}
                </span>
              </div>
              <div className="text-sm">{it.descrizione}</div>
              {it.dettagli && Object.keys(it.dettagli).length > 0 && (
                <details className="text-xs text-fg-muted">
                  <summary className="cursor-pointer">{"dettagli"}</summary>
                  <pre className="bg-primary-deep/40 p-2 rounded mt-1 overflow-x-auto">
                    {JSON.stringify(it.dettagli, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}

      {data && (data.has_more || offset > 0) && (
        <div className="flex gap-2 justify-center">
          <button
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            className="btn-ghost"
          >
            {tr("← precedenti")}
          </button>
          <button
            disabled={!data.has_more}
            onClick={() => setOffset(offset + LIMIT)}
            className="btn-ghost"
          >
            {tr("successivi →")}
          </button>
        </div>
      )}
    </MobileShell>
  );
}
